import { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { FaCheckCircle, FaDownload, FaPlus } from "react-icons/fa";
import Breadcrumb from "../../components/Breadcrumb/Breadcrumb";
import ProductGrid from "../../components/ProductGrid/ProductGrid";
import Loader from "../../components/Loader/Loader";
import Button from "../../components/ui/Button";
import SectionTitle from "../../components/ui/SectionTitle";
import { useQuote } from "../../context/QuoteContext";
import {
 getProductById,
 getProductsByCategory,
} from "../../services/productService";
import { RichText, stripHtml } from "../../utils/richText";

export default function ProductDetails() {
 const { id } = useParams();
 const [product, setProduct] = useState(null);
 const [activeImage, setActiveImage] = useState("");
 const [selectedVariant, setSelectedVariant] = useState(null);
 const [zoomPosition, setZoomPosition] = useState({ x: 50, y: 50 });
 const [related, setRelated] = useState([]);
 const [loading, setLoading] = useState(true);
 const [notFound, setNotFound] = useState(false);
 const { addToQuote } = useQuote();

 useEffect(() => {
  let alive = true;
  setLoading(true);
  setNotFound(false);
  getProductById(id)
   .then((productData) => {
    if (!alive) return;
    setProduct(productData);
    setActiveImage(productData.image);
    setSelectedVariant(productData.variants?.[0] || null);
    return getProductsByCategory(productData.category).then((items) => {
     if (alive)
      setRelated(
       items.filter((item) => item.id !== productData.id).slice(0, 4),
      );
    });
   })
   .catch(() => {
    if (alive) setNotFound(true);
   })
   .finally(() => {
    if (alive) setLoading(false);
   });
  return () => {
   alive = false;
  };
 }, [id]);

 if (loading) {
  return (
   <section className="container-pad grid min-h-[60vh] place-items-center py-12">
    <Loader />
   </section>
  );
 }

 if (notFound || !product) return <Navigate to="/404" replace />;

 const productImages =
  Array.isArray(product.images) && product.images.length
   ? product.images
   : [product.image];
 const currentImage = activeImage || productImages[0];
 const variantPrice = Number(selectedVariant?.price || 0);
 const originalPrice =
  variantPrice > 0 ? variantPrice : Number(product.price || 0);
 const discountType = product.discountType || "none";
 const discountValue = Number(product.discountValue || 0);
 const selectedDiscountAmount =
  discountType === "percent"
   ? Math.round((originalPrice * Math.min(discountValue, 100)) / 100)
   : discountType === "amount"
     ? Math.min(discountValue, originalPrice)
     : 0;
 const baseDisplayPrice =
  originalPrice > 0
   ? Math.max(0, originalPrice - selectedDiscountAmount)
   : Number(product.finalPrice ?? product.price ?? 0);
 const displayGstAmount = Math.round(
  (baseDisplayPrice * Number(product.gstPercent || 0)) / 100,
 );
 const displayPrice =
  Number(product.gstPercent || 0) > 0
   ? baseDisplayPrice + displayGstAmount
   : baseDisplayPrice;
 const hasDiscountPrice = originalPrice > 0 && originalPrice > baseDisplayPrice;
 const rating = Number(product.rating || 4.5);
 const optionGroupName = product.optionGroupName || "Size";
 const selectedStock = Number(selectedVariant?.stock ?? product.stock ?? 0);
 const selectedUnit = selectedVariant?.unit || product.unit || "pcs";
 const isKgUnit = String(selectedUnit).trim().toLowerCase() === "kg";
 const priceUnitNote = isKgUnit ? "per kg" : "";
 const isSelectedOutOfStock = selectedStock <= 0;
 const handleDownloadPdf = () => {
  openProductPdf(
   product,
   selectedVariant,
   currentImage,
   displayPrice,
   rating,
   "/logo.jpeg",
   displayGstAmount,
  );
 };
 const updateZoomPosition = (event) => {
  const rect = event.currentTarget.getBoundingClientRect();
  const x = ((event.clientX - rect.left) / rect.width) * 100;
  const y = ((event.clientY - rect.top) / rect.height) * 100;
  setZoomPosition({
   x: Math.min(100, Math.max(0, x)),
   y: Math.min(100, Math.max(0, y)),
  });
 };

 return (
  <section className="container-pad py-8 sm:py-10">
   <Breadcrumb
    items={[
     { label: "Products", to: "/products" },
     { label: stripHtml(product.name) },
    ]}
   />

   <div className="grid gap-8 lg:grid-cols-[minmax(360px,520px)_minmax(0,1fr)] lg:items-start">
    <aside className="lg:sticky lg:top-28 lg:self-start">
     <div
      className="group relative grid h-[320px] cursor-zoom-in place-items-center overflow-hidden rounded-xl border border-line bg-white sm:h-[390px] lg:h-[430px] xl:h-[460px]"
      onMouseMove={updateZoomPosition}
     >
      <img
       src={currentImage}
       alt={stripHtml(product.name)}
       className="h-full w-full object-cover object-center transition-transform duration-200 ease-out group-hover:scale-[1.9]"
       style={{
        transformOrigin: `${zoomPosition.x}% ${zoomPosition.y}%`,
       }}
      />
      <span className="pointer-events-none absolute bottom-3 left-3 rounded-full bg-black/60 px-3 py-1 text-xs font-bold text-white opacity-0 transition group-hover:opacity-100">
       Hover to zoom
      </span>
     </div>
     <div className="mt-3 grid grid-cols-5 gap-2 pb-2">
      {productImages.slice(0, 5).map((image, index) => (
       <button
        key={image}
        onClick={() => setActiveImage(image)}
        className={`grid h-14 place-items-center overflow-hidden rounded-lg border bg-white p-1 transition hover:border-primary sm:h-16 ${currentImage === image ? "border-primary" : "border-line"}`}
        aria-label={`Product image thumbnail ${index + 1}`}
       >
        <img src={image} alt="" className="h-full w-full object-contain object-center" />
       </button>
      ))}
     </div>
    </aside>

    <div className="min-w-0 space-y-5">
     <div className="rounded-xl border border-line bg-white p-5 sm:p-6">
      <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold uppercase tracking-wide text-primary">
       {product.categoryName || product.category}
      </span>
      <RichText
       as="h1"
       className="mt-3 text-2xl font-bold leading-tight text-dark sm:text-3xl"
       value={product.name}
      />
      <p className="mt-2 text-base font-semibold text-primary">
       {product.brand}
      </p>
     </div>

     {product.variants?.length ? (
      <div className="rounded-xl border border-line bg-base p-4">
       <p className="text-sm font-semibold text-dark">
        {optionGroupName}: <span className="text-primary">{selectedVariant?.label}</span>
       </p>
       <div className="mt-3 flex flex-wrap gap-2">
        {product.variants.map((variant) => (
         <button
          key={variant.label}
          type="button"
          onClick={() => setSelectedVariant(variant)}
          className={`rounded-xl border px-4 py-2 text-sm font-semibold transition ${
           selectedVariant?.label === variant.label
            ? "border-primary bg-emerald-50 text-primary"
            : Number(variant.stock || 0) <= 0
              ? "border-red-100 bg-red-50 text-danger"
              : "border-line bg-white text-dark hover:border-primary hover:text-primary"
          }`}
          title={Number(variant.stock || 0) <= 0 ? "Out of stock" : ""}
         >
          {variant.label}
          {Number(variant.stock || 0) <= 0 ? " - Out of stock" : ""}
         </button>
        ))}
       </div>
      </div>
     ) : null}

     {product.colorOptions?.length ? (
      <div className="rounded-xl border border-line bg-base p-4">
       <p className="text-sm font-semibold text-dark">
        Available colors: <span className="text-primary">{product.colorOptions.length} options</span>
       </p>
       <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
        {product.colorOptions.map((color) => (
         <div
          key={color.name}
          className="flex items-center gap-2 rounded-xl border border-line bg-white px-3 py-2"
         >
          <span
           className="h-5 w-5 rounded-full border border-slate-200"
           style={{ backgroundColor: color.hex }}
          />
          <span className="text-xs font-bold text-dark">{color.name}</span>
         </div>
        ))}
       </div>
      </div>
     ) : null}

     <div className="grid gap-2 sm:grid-cols-3">
      <Info
       title="MOQ"
       value={`${selectedVariant?.minQty || product.minQty || 1} ${selectedVariant?.unit || product.unit || "pcs"}`}
      />
      <Info
       title="Stock"
       value={
        isSelectedOutOfStock
         ? "Out of stock"
         : `${selectedStock} ${selectedUnit}`
       }
      />
      <Info
       title="GST"
       value={
        Number(product.gstPercent || 0) > 0
         ? `${product.gstPercent}% included`
         : "As applicable"
       }
      />
     </div>

     {displayPrice ? (
      <div className="rounded-lg border border-line bg-base p-2.5">
       <div className="grid gap-2 sm:grid-cols-3">
        {hasDiscountPrice ? (
         <PriceBox
          label="Original price"
          value={originalPrice}
          crossed
          note={product.discountLabel}
          priceSuffix={isKgUnit ? "/ kg" : ""}
         />
        ) : null}
        <PriceBox
         label="Actual price"
         value={baseDisplayPrice}
         highlight={!hasDiscountPrice}
         note={
          hasDiscountPrice
           ? `After discount${priceUnitNote ? " - per kg" : ""}`
           : `Without GST${priceUnitNote ? " - per kg" : ""}`
         }
         priceSuffix={isKgUnit ? "/ kg" : ""}
        />
        <PriceBox
         label="With GST price"
         value={displayPrice}
         highlight
         note={
          Number(product.gstPercent || 0) > 0
           ? `Incl. ${product.gstPercent}% GST${priceUnitNote ? " - per kg" : ""}`
           : `GST as applicable${priceUnitNote ? " - per kg" : ""}`
         }
         priceSuffix={isKgUnit ? "/ kg" : ""}
        />
       </div>
       <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <span className="rounded-md bg-orange-50 px-2 py-0.5 text-[11px] font-bold text-primary">
         Rating {rating.toFixed(1)}
        </span>
        {hasDiscountPrice ? (
         <span className="rounded-md bg-emerald-50 px-2 py-0.5 text-[11px] font-bold text-success">
          {product.discountLabel}
         </span>
        ) : null}
       </div>
      </div>
     ) : null}

     <div className="flex flex-col gap-3 sm:flex-row">
      <Button
       onClick={() => {
        if (!isSelectedOutOfStock) addToQuote(product, selectedVariant);
       }}
       disabled={isSelectedOutOfStock}
       variant={isSelectedOutOfStock ? "secondary" : "primary"}
       className={
        isSelectedOutOfStock
         ? "border-red-100 bg-red-50 text-danger hover:border-red-100 hover:text-danger"
         : ""
       }
      >
       <FaPlus /> {isSelectedOutOfStock ? "Out of stock" : "Add To Quote"}
      </Button>
      <Button variant="secondary" onClick={handleDownloadPdf}>
       <FaDownload /> Download PDF
      </Button>
     </div>

     <div className="rounded-xl border border-line bg-white p-5 sm:p-6">
      <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
       Description
      </p>
      <RichText
       as="p"
       className="mt-3 text-base leading-7 text-body"
       value={product.shortDescription}
      />
     </div>

     <div className="grid gap-5">
      <div className="rounded-xl border border-line bg-white p-5 sm:p-6">
       <h2 className="text-2xl font-bold text-dark">Specifications</h2>
       <div className="mt-5 overflow-hidden rounded-xl border border-line">
        {product.specifications.map((spec, index) => (
         <div
          key={`${spec}-${index}`}
          className="grid grid-cols-[56px_1fr] border-b border-line last:border-b-0"
         >
          <div className="grid place-items-center bg-slate-50 px-3 py-3 text-primary">
           <FaCheckCircle className="h-4 w-4" aria-hidden="true" />
          </div>
          <RichText
           as="div"
           className="px-4 py-3 text-sm text-body"
           value={spec}
          />
         </div>
        ))}
       </div>
       <DetailList title="Features" items={product.features} />
      </div>
      <aside className="rounded-xl border border-line bg-white p-5 sm:p-6">
       <h2 className="text-2xl font-bold text-dark">Available </h2>
       <div className="mt-5 flex flex-wrap gap-2">
        {product.sizes.map((size, index) => (
         <span
          key={`${size}-${index}`}
          className={`rounded-xl px-4 py-2 text-sm font-bold ${selectedVariant?.label === size ? "bg-emerald-50 text-primary" : "bg-slate-50 text-dark"}`}
         >
          {size}
         </span>
        ))}
       </div>
       <div className="mt-8 rounded-xl bg-emerald-50 p-5">
        <h3 className="font-semibold text-dark">Need bulk supply?</h3>
        <p className="mt-2 text-sm leading-6 text-body">
         Add this product to quote and mention quantity, connector type, delivery
         city and preferred dispatch timeline.
        </p>
       </div>
      </aside>
     </div>
    </div>
   </div>

   <div className="mt-14">
    <SectionTitle
     eyebrow="Related products"
     title="More products in this category"
    />
    <ProductGrid products={related} />
   </div>
  </section>
 );
}

function Info({ title, value }) {
 return (
  <div className="rounded-lg border border-line bg-white px-3 py-2.5">
   <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">
    {title}
   </p>
   <p className="mt-1 text-sm font-bold leading-tight text-dark">{value}</p>
  </div>
 );
}

function PriceBox({
 label,
 value,
 note,
 priceSuffix = "",
 crossed = false,
 highlight = false,
}) {
 return (
  <div
   className={`rounded-md border px-2.5 py-2 ${
    highlight ? "border-primary bg-emerald-50" : "border-line bg-white"
   }`}
  >
   <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
    {label}
   </p>
   <strong
    className={`mt-0.5 block text-base font-bold leading-tight sm:text-lg ${
     crossed ? "text-slate-500 line-through" : "text-dark"
    }`}
   >
    ₹{Number(value || 0).toLocaleString("en-IN")}
    {priceSuffix ? (
     <span className="ml-1 text-xs font-extrabold text-slate-500">
      {priceSuffix}
     </span>
    ) : null}
   </strong>
   {note ? (
    <span className="mt-0.5 block text-[10px] font-bold leading-tight text-primary">
     {note}
    </span>
   ) : null}
  </div>
 );
}

function DetailList({ title, items }) {
 return (
  <div className="mt-6">
   <h3 className="text-xl font-semibold text-dark">{title}</h3>
   <ul className="mt-3 grid gap-2 sm:grid-cols-2">
    {items.map((item, index) => (
     <li
      key={`${item}-${index}`}
      className="rounded-xl bg-slate-50 px-4 py-3 text-sm font-semibold text-body"
     >
      <RichText value={item} />
     </li>
    ))}
   </ul>
  </div>
 );
}

function openProductPdf(
 product,
 selectedVariant,
 image,
 displayPrice,
 rating,
 logoUrl,
 gstAmount = 0,
) {
 const popup = window.open("", "_blank", "width=980,height=720");
 if (!popup) {
  alert("Please allow popups to download the product PDF.");
  return;
 }

 const unit = selectedVariant?.unit || product.unit || "pcs";
 const rows = [
  ["Product", stripHtml(product.name)],
  ["Brand", product.brand || "Mozo EV"],
  ["Category", product.categoryName || product.category],
  ["Rating", rating ? `${rating.toFixed(1)} / 5` : "Not available"],
  [
   "Price",
   displayPrice
    ? `Rs. ${Number(displayPrice).toLocaleString("en-IN")}`
    : "On request",
  ],
  [
   "GST",
   Number(product.gstPercent || 0) > 0
    ? `${product.gstPercent}% (${Number(gstAmount || product.gstAmount || 0).toLocaleString("en-IN")})`
    : "As applicable",
  ],
  ["MOQ", `${selectedVariant?.minQty || product.minQty || 1} ${unit}`],
  ["Stock", `${selectedVariant?.stock ?? product.stock ?? 0} ${unit}`],
 ];

 const html = `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(stripHtml(product.name))} - Product Details</title>
  <style>
    * { box-sizing: border-box; }
    body { margin: 0; padding: 28px; font-family: Arial, sans-serif; color: #062b3a; background: #fff; }
    .header { display: flex; justify-content: space-between; gap: 20px; border-bottom: 3px solid #18a84a; padding-bottom: 18px; }
    .brand-row { display: flex; align-items: center; gap: 12px; }
    .brand-row img { width: 58px; height: 42px; object-fit: contain; }
    .brand { color: #18a84a; font-size: 13px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; }
    h1 { margin: 8px 0 0; font-size: 28px; line-height: 1.2; }
    .date { color: #64748b; font-size: 13px; text-align: right; }
    .hero { display: grid; grid-template-columns: 260px 1fr; gap: 24px; margin-top: 24px; align-items: start; }
    .image { border: 1px solid #dcebe3; border-radius: 12px; padding: 14px; height: 260px; display: grid; place-items: center; }
    .image img { max-width: 100%; max-height: 100%; object-fit: contain; }
    .summary { border: 1px solid #dcebe3; border-radius: 12px; overflow: hidden; }
    .row { display: grid; grid-template-columns: 150px 1fr; border-bottom: 1px solid #dcebe3; }
    .row:last-child { border-bottom: 0; }
    .label { background: #f6fbf8; padding: 10px 12px; font-size: 12px; font-weight: 700; color: #31515b; text-transform: uppercase; }
    .value { padding: 10px 12px; font-size: 14px; }
    .desc { margin-top: 20px; color: #31515b; line-height: 1.65; font-size: 14px; }
    .section { margin-top: 22px; }
    h2 { margin: 0 0 10px; font-size: 18px; }
    ul { margin: 0; padding-left: 18px; color: #31515b; line-height: 1.6; }
    .chips { display: flex; flex-wrap: wrap; gap: 8px; }
    .chip { border: 1px solid #dcebe3; border-radius: 999px; padding: 7px 10px; font-size: 12px; }
    .footer { margin-top: 28px; border-top: 1px solid #dcebe3; padding-top: 14px; color: #64748b; font-size: 12px; }
    @media print {
      body { padding: 20px; }
      .no-print { display: none; }
      .hero { grid-template-columns: 220px 1fr; }
      .image { height: 220px; }
    }
  </style>
</head>
<body>
  <div class="header">
    <div>
      <div class="brand-row">
        <img src="${escapeAttr(logoUrl)}" alt="Mozo EV" />
        <div>
          <div class="brand">Mozo EV Product Datasheet</div>
          <h1>${escapeHtml(stripHtml(product.name))}</h1>
        </div>
      </div>
    </div>
    <div class="date">Generated on<br/>${new Date().toLocaleDateString("en-IN")}</div>
  </div>
  <div class="hero">
    <div class="image"><img src="${escapeAttr(image)}" alt="${escapeAttr(stripHtml(product.name))}" /></div>
    <div class="summary">${rows.map(([label, value]) => `<div class="row"><div class="label">${escapeHtml(label)}</div><div class="value">${escapeHtml(value)}</div></div>`).join("")}</div>
  </div>
  <p class="desc">${escapeHtml(stripHtml(product.shortDescription || "EV battery product for B2B supply."))}</p>
  ${renderPdfSection("Specifications", product.specifications)}
  ${renderPdfSection("Available Sizes", product.sizes, true)}
  ${renderPdfSection("Features", product.features)}
  <div class="footer">This product detail sheet is generated from the Mozo EV website. Contact sales for final quotation, GST, delivery and availability confirmation.</div>
  <script>
    window.addEventListener("load", function () {
      setTimeout(function () {
        window.print();
      }, 400);
    });
  </script>
</body>
</html>`;

 popup.document.open();
 popup.document.write(html);
 popup.document.close();
}

function renderPdfSection(title, items = [], chips = false) {
 const safeItems = Array.isArray(items) ? items.filter(Boolean) : [];
 if (!safeItems.length) return "";
 if (chips) {
  return `<div class="section"><h2>${escapeHtml(title)}</h2><div class="chips">${safeItems.map((item) => `<span class="chip">${escapeHtml(item)}</span>`).join("")}</div></div>`;
 }
 return `<div class="section"><h2>${escapeHtml(title)}</h2><ul>${safeItems.map((item) => `<li>${escapeHtml(item)}</li>`).join("")}</ul></div>`;
}

function escapeHtml(value = "") {
 return String(value)
  .replaceAll("&", "&amp;")
  .replaceAll("<", "&lt;")
  .replaceAll(">", "&gt;")
  .replaceAll('"', "&quot;")
  .replaceAll("'", "&#039;");
}

function escapeAttr(value = "") {
 return escapeHtml(value).replaceAll("`", "&#096;");
}






